import { useQuery } from "@tanstack/react-query";
import { tutorApiService } from "../../services/tutorApi";
import { SuggestionCard } from "../../types/api.types";
import { SUGGESTION_CARDS } from "../../constants";

/**
 * Suggestion Cards Query Hook
 *
 * Empty State UI에 표시할 제안 카드를 가져옵니다.
 * 레거시 REACT_APP_GREETING_MSG API를 호출하여 동적으로 데이터를 가져오며,
 * 에러 발생 시 정적 상수 SUGGESTION_CARDS를 fallback으로 사용합니다.
 *
 * 특징:
 * - 1시간 staleTime (suggestion은 자주 변경되지 않음)
 * - 에러 시 정적 fallback 제공
 * - 모드별 suggestion 제공 (tutor/socrates)
 *
 * @param mode "tutor" | "socrates"
 * @returns React Query result with suggestion cards
 *
 * @example
 * ```tsx
 * const { data: suggestions } = useSuggestionsQuery('tutor');
 * // suggestions는 항상 SuggestionCard[] (에러 시 정적 fallback)
 * ```
 */
export const useSuggestionsQuery = (mode: "tutor" | "socrates") => {
  return useQuery<SuggestionCard[]>({
    queryKey: ["suggestions", mode],
    queryFn: async () => {
      try {
        return await tutorApiService.fetchSuggestions(mode);
      } catch (error) {
        // API 에러 시 정적 상수 반환 (fallback)
        return [...SUGGESTION_CARDS] as SuggestionCard[];
      }
    },
    staleTime: 1000 * 60 * 60, // 1시간
    gcTime: 1000 * 60 * 60 * 2, // 2시간 (구 cacheTime)
    retry: 1, // 실패 시 1번만 재시도
    refetchOnWindowFocus: false, // suggestion은 window focus 시 재요청 불필요
    initialData: [...SUGGESTION_CARDS] as SuggestionCard[], // 초기 로딩 시 정적 상수 표시
  });
};
